import type { GetStaticPathsResult } from 'next';
import { ConfigProvider } from 'shared/config/ConfigContext';
import ppData from 'shared/seo/data/pp.json';
import { ExtraLinkTree } from 'shared/seo/ExtraLinkTree';
import { LinkTree } from 'shared/seo/LinkTree';
import { PopularCities } from 'shared/seo/PopularCities';
import { PopularCountries } from 'shared/seo/PopularCountries';
import { DestinationPage as OldDestinationPage } from '@/components/DestinationPage';
import { cityLevel1Factory, prepackageSearchFactory } from '@/components/factory';
import { DestinationPage as NewDestinationPage } from '@/components/NewDestinationPage';
import { type OfferItem, Offers } from '@/components/offers/Offers';
import { config, feConfig } from '@/config';
import { getCountriesForDestination, prepackageCities, prepackageCountries, prepackageMapping } from '@/data';
import { verticals } from '@/data/verticals';
import type { CityPageWithOffersProps, SeoPageContext } from '@/interfaces/pages';
import { fetchAutocompleteBounds } from '@/lib/autocompleteBounds';
import { fetchPagesMetadata } from '@/lib/pages-metadata';
import { pageContentUrl, pageFor } from '@/lib/prepackageContent';

const ppOfferUrl = config.get('prepackage.offers');
const allPagesMetadataUrl = config.get('prepackage.allCountryPlaceDestinationPagesMetadata');

const locationQueryParams = (codes: string[]): string => codes.map((code) => `to=${encodeURI(code)}`).join('&');

export const getStaticPaths = async (): Promise<GetStaticPathsResult> => {
    const allPagesMetadata: Record<string, string>[] = await fetchPagesMetadata(allPagesMetadataUrl).catch((err) => {
        console.error(err);
        return err;
    });

    const destinations = allPagesMetadata.map((page: { place: string; country: string }) => ({
        params: {
            cityLevel1: page.place,
            countryLevel1: page.country,
        },
    }));

    return {
        paths: destinations,
        fallback: false,
    };
};

export const getStaticProps = async (context: SeoPageContext) => {
    const { countryLevel1, cityLevel1 } = context.params;
    const page = await pageFor(pageContentUrl.forCountryPlaceDestination(countryLevel1, cityLevel1));

    const destination = prepackageCities.find(
        (place) => place.countryPath === countryLevel1.toLowerCase() && place.cityPath === cityLevel1.toLowerCase(),
    );
    const destinationCountry = prepackageCountries.find((place) => place.countryPath === countryLevel1.toLowerCase());

    if (!page.htmlContent && (!destination || !destinationCountry)) {
        return {
            notFound: true,
        };
    }

    const popularCities = prepackageMapping[destination.id] ?? prepackageMapping[destinationCountry.id] ?? [];
    const popularCountries = getCountriesForDestination(destinationCountry, prepackageCountries);

    const [offers, bounds] = await Promise.all([
        fetch(`${ppOfferUrl}?${locationQueryParams(destination.locationCodes)}`)
            .then((res) => (res.ok ? res.json() : Promise.reject(`Fetching offers failed (${res.status}): ${res.url}`)))
            .catch((err) => {
                console.warn(err);
                return [] as OfferItem[];
            }),
        fetchAutocompleteBounds(config.get('prepackage.autocomplete'), `${destination.city}, ${destination.country}`),
    ]);

    return {
        props: {
            destination: {
                ...destination,
                bounds,
            },
            content: page.htmlContent ?? null,
            popularCities,
            popularCountries,
            offers,
            config: feConfig,
        },
        revalidate: 3600,
    };
};

const CityLevel1 = ({ destination, content, popularCities, popularCountries, offers, config }: CityPageWithOffersProps) => {
    const factory = cityLevel1Factory(destination, ppData, prepackageSearchFactory());

    const DestinationPage = content ? NewDestinationPage : OldDestinationPage;

    return (
        <ConfigProvider config={config}>
            <DestinationPage factory={factory} vertical={verticals.prepackage} data={ppData}>
                {content ? <div className="mb-64" dangerouslySetInnerHTML={{ __html: content }} /> : null}
                <Offers
                    destination={destination.city}
                    offers={offers}
                    vertical={verticals.prepackage}
                    heading={ppData.lang.headingOffers}
                    showMap={false}
                />
                <PopularCities
                    destination={destination}
                    cities={popularCities}
                    cdnUrl={ppData.cdnPath}
                    verticalUrl={ppData.basePath}
                    lang={ppData.lang}
                />
                <PopularCountries
                    destination={destination}
                    countries={popularCountries}
                    cdnUrl={ppData.cdnPath}
                    verticalUrl={ppData.basePath}
                    lang={ppData.lang}
                />
                <LinkTree
                    heading={ppData.lang.linkTree.heading}
                    description={ppData.lang.linkTree.description}
                    data={ppData.linkTree}
                    url={ppData.basePath}
                />
                <ExtraLinkTree
                    heading={ppData.lang.extraLinkTree.heading}
                    linkPrefix={ppData.lang.extraLinkTree.prefix}
                    data={ppData.extraLinkTree}
                    url={ppData.basePath}
                />
            </DestinationPage>
        </ConfigProvider>
    );
};

export default CityLevel1;
